import {
  Box,
  Chip,
  IconButton,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';

import ExpandChevron from '@mui/icons-material/KeyboardArrowDown';
import { GroupedLayUp } from '../LayingUp';
import LayUpBoxIcon from './LayUpIcon';

const PREFIX = 'LayUpTable';

const classes = {
  root: `${PREFIX}-root`,
  table: `${PREFIX}-table`,
  tableBody: `${PREFIX}-tableBody`,
};

const CustomTableContainer = styled(TableContainer)(
  ({ theme }: { theme: Theme }) => ({
    [`&.${classes.root}`]: {
      width: '100%',
    },

    [`& .${classes.table}`]: {
      borderSpacing: '0 10px',
    },

    [`& .${tableCellClasses.root}`]: {
      paddingTop: '20px',
      paddingRight: '16px',
      paddingBottom: '16px',
      paddingLeft: '16px',
      borderBottom: 'none',
    },

    [`& .${classes.tableBody}`]: {
      backgroundColor: theme.palette.background.paper,

      '&:not(:last-of-type)::after': {
        content: '""',
        display: 'table-row',
        height: '8px',
        backgroundColor: theme.palette.background.default,
      },

      [`& tr:first-of-type .${tableCellClasses.root}:first-of-type`]: {
        borderTopLeftRadius: 16,
      },

      [`& tr:first-of-type .${tableCellClasses.root}:last-of-type`]: {
        borderTopRightRadius: 16,
      },

      [`& tr:last-of-type .${tableCellClasses.root}:first-of-type`]: {
        borderBottomLeftRadius: 16,
      },

      [`& tr:last-of-type .${tableCellClasses.root}:last-of-type`]: {
        borderBottomRightRadius: 16,
      },
    },
  })
);

interface Prop {
  weekdays: Dayjs[];
  layups: GroupedLayUp[];
  expandedIds: Set<string>;
  onToggle: (id: string) => void;
  onGroupClicked: (
    consultantId: string,
    operatingListId: string,
    date: Date
  ) => void;
}

function LayUpTable({
  weekdays,
  layups,
  expandedIds,
  onToggle,
  onGroupClicked,
}: Prop) {
  return (
    <CustomTableContainer className={classes.root}>
      <Table
        className={classes.table}
        aria-labelledby="LayUpTable"
        size="small"
        aria-label="layUp summary table">
        <TableHead>
          <TableRow>
            <TableCell />
            {weekdays.map((day) => (
              <TableCell key={day.format('YYYY-MM-DD')}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}>
                  <Typography variant="body2">{day.format('ddd')}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {day.format('D')}
                  </Typography>
                </Box>
              </TableCell>
            ))}

            <TableCell />
          </TableRow>
        </TableHead>

        {layups.map((layup, i) => {
          const isExpanded = expandedIds.has(layup.consultantFullName);
          return (
            <TableBody className={classes.tableBody} key={layup.consultantId}>
              <TableRow>
                <TableCell sx={{ width: '100%' }}>
                  <Stack
                    direction={'row'}
                    gap={2}
                    alignItems={'center'}
                    width={'100%'}>
                    <LayUpBoxIcon status={layup.status} />
                    <Typography variant="subtitle1">
                      {layup.consultantFullName}
                    </Typography>
                  </Stack>
                </TableCell>
                {weekdays.map((day) => {
                  const match = layup.dateGroups.find((d) =>
                    dayjs(d.procedureDate).isSame(day, 'day')
                  )!;

                  return (
                    <TableCell key={day.format('YYYY-MM-DD')}>
                      <LayUpBoxIcon
                        status={match?.status}
                        size={32}
                        translucent={isExpanded}
                      />
                    </TableCell>
                  );
                })}

                <TableCell>
                  <Stack
                    direction={'row'}
                    alignItems={'center'}
                    justifyContent={'right'}
                    spacing={2}>
                    {layup.nbLayupsWithMissingItems > 0 && (
                      <Tooltip
                        title={
                          <Typography variant="caption">
                            Procedures missing items
                          </Typography>
                        }>
                        <Chip
                          label={layup.nbLayupsWithMissingItems}
                          variant="outlined"
                          color="error"
                          size="medium"
                          sx={{ minWidth: 48 }}
                        />
                      </Tooltip>
                    )}

                    <IconButton
                      onClick={() => onToggle(layup.consultantFullName)}>
                      <ExpandChevron />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>

              {isExpanded &&
                layup.operatingLists.map((operatingList) => (
                  <TableRow key={operatingList.id}>
                    <TableCell>
                      <Typography variant="subtitle2">
                        {operatingList.name}
                      </Typography>
                    </TableCell>

                    {weekdays.map((day) => {
                      const match = operatingList.dateGroups.find((d) =>
                        dayjs(d.procedureDate).isSame(day, 'day')
                      );

                      return (
                        <TableCell
                          key={`${operatingList.id}-${day.format('YYYY-MM-DD')}`}>
                          <IconButton
                            sx={{
                              p: 0,
                              width: 'auto',
                              height: 'auto',
                            }}
                            onClick={() => {
                              if (match) {
                                onGroupClicked(
                                  layup.consultantId,
                                  operatingList.id,
                                  day.toDate()
                                );
                              }
                            }}>
                            <LayUpBoxIcon status={match?.status} size={32} />
                          </IconButton>
                        </TableCell>
                      );
                    })}
                    <TableCell />
                  </TableRow>
                ))}
            </TableBody>
          );
        })}
      </Table>
    </CustomTableContainer>
  );
}

export default LayUpTable;
